import React, { useEffect, useState, useCallback } from "react";
import "../styles-quality-control/qa_style.css";
import {
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { toast } from "react-toastify";
import CustomRadioGroup from "../CustomRadioGroup/CustomRadioGroup";
import googleDriveAxios from "../../common/googleDriveAxios";
import CameraCapture from "../CameraCapture/CameraCapture";
import ImageList from "../ImageList/ImageList";

interface QAPlumbingChecklist {
  title: string | null;
  notes: string | null;
  status: string | null;
  isDisabled?: boolean;
}

const QualityControlPlumbingChecklist: React.FC<{
  chassisNo: string;
  setModel: React.Dispatch<React.SetStateAction<string>>;
  model: string;
  setCheckedBy: React.Dispatch<React.SetStateAction<string>>;
  isDisabled: boolean;
}> = ({ chassisNo, setModel, model, setCheckedBy, isDisabled }) => {
  const axiosPrivate = useAxiosPrivate();

  const [loading, setLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<boolean>(false);
  const [checkedBy, setLocalCheckedBy] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [accessoryCheckList, setAccessoryCheckList] = useState<
    QAPlumbingChecklist[]
  >([]);
  const [gasAndPlanList, setGasAndPlanList] = useState<QAPlumbingChecklist[]>(
    []
  );
  const [waterLeakCheckList, setWaterLeakCheckList] = useState<
    QAPlumbingChecklist[]
  >([]);
  const [completeStatus, setCompleteStatus] = useState<string>("Incomplete");
  const hasUnsavedChanges = chassisNo;

  const [activeCheckpointList, setActiveCheckpointList] = useState<any[]>([]);

  const checkCheckpointRecord = React.useCallback(async () => {
    if (chassisNo) {
      try {
        const queryParams = new URLSearchParams({
          chassis_no: "DC" + chassisNo,
          dept_name: "qc_plumbing",
        }).toString();

        const response = await googleDriveAxios.post(
          `/checkpoint-image-record?${queryParams}`
        );
        const data = response.data;
        setActiveCheckpointList(data.recorded_checkpoint_list);
        console.log(data.recorded_checkpoint_list);
      } catch (error) {
        console.error("Error checking images:", error);
      }
    }
  }, [chassisNo]);

  useEffect(() => {
    checkCheckpointRecord();
  }, [chassisNo, checkCheckpointRecord]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const prepareData = useCallback(async () => {
    if (!chassisNo || chassisNo.length < 4 || chassisNo.length > 5) {
      setAccessoryCheckList([]);
      setGasAndPlanList([]);
      setWaterLeakCheckList([]);
      setModel("");
      setLocalCheckedBy("");
      setCheckedBy("");
      setCompleteStatus("Incomplete");
      setApiError(false);
      return;
    }

    setLoading(true);
    setApiError(false);
    try {
      const response = await axiosPrivate.post(
        `prepare_qc_plumbing/${chassisNo}`
      );
      const data = response.data;

      const accessoryData = (data.accessoryCheckList || []).map(
        (item: QAPlumbingChecklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const gasAndPlanData = (data.gasAndPlanList || []).map(
        (item: QAPlumbingChecklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const waterLeakData = (data.waterLeakCheckList || []).map(
        (item: QAPlumbingChecklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );

      setAccessoryCheckList(accessoryData);
      setGasAndPlanList(gasAndPlanData);
      setWaterLeakCheckList(waterLeakData);
      setModel(data.model || "");
      setLocalCheckedBy(data.checkedBy || "");
      setCheckedBy(data.checkedBy || "");

      const formattedDate = data.createdOn
        ? new Date(data.createdOn).toLocaleDateString("en-CA", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
        : "";
      setDate(formattedDate || "");

      const allItems = [...accessoryData, ...gasAndPlanData, ...waterLeakData];
      const isComplete =
        allItems.length > 0 &&
        allItems.every(
          (item) =>
            item.status === "pass" ||
            item.status === "fail" ||
            item.status === "n/a"
        );
      setCompleteStatus(isComplete ? "Complete" : "Incomplete");
      console.log(
        "prepareData - isComplete:",
        isComplete,
        "completeStatus:",
        isComplete ? "Complete" : "Incomplete",
        "loading:",
        false
      );
    } catch (error: any) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 500) {
        setApiError(true);
      } else {
        toast.error("Failed to fetch data.");
      }
    } finally {
      setLoading(false);
      console.log("prepareData - loading reset to:", false);
    }
  }, [
    chassisNo,
    setModel,
    setCheckedBy,
    axiosPrivate,
    setAccessoryCheckList,
    setGasAndPlanList,
    setWaterLeakCheckList,
    setLocalCheckedBy,
    setCompleteStatus,
    setApiError,
    setLoading,
    setDate,
  ]);

  useEffect(() => {
    prepareData();
  }, [chassisNo, setModel, setCheckedBy, prepareData]);
  const handleCheckedByChange = (event: any) => {
    if (isDisabled && completeStatus === "Complete") {
      console.log(
        "Checked By change blocked: isDisabled=",
        isDisabled,
        "completeStatus=",
        completeStatus
      );
      return;
    }
    setLocalCheckedBy(event.target.value);
    setCheckedBy(event.target.value);
  };

  const saveData = async () => {
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }

    setLoading(true);
    console.log("saveData - loading set to:", true);
    try {
      const allChecklists = [
        ...accessoryCheckList,
        ...gasAndPlanList,
        ...waterLeakCheckList,
      ];

      const isComplete =
        allChecklists.length > 0 &&
        allChecklists.every(
          (item) =>
            item.status === "pass" ||
            item.status === "fail" ||
            item.status === "n/a"
        );
      const newCompleteStatus = isComplete ? "Complete" : "Incomplete";
      setCompleteStatus(newCompleteStatus);
      console.log(
        "saveData - isComplete:",
        isComplete,
        "completeStatus:",
        newCompleteStatus
      );

      const payload = {
        chassisNo: chassisNo,
        model: model,
        accessoryCheckList: accessoryCheckList.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        gasAndPlanList: gasAndPlanList.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        waterLeakCheckList: waterLeakCheckList.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        checkedBy: checkedBy,
        createdOn: date,
        completeStatus: newCompleteStatus,
      };

      await toast.promise(
        axiosPrivate.post("create_or_update_qa_plumbing", payload),
        {
          pending: "Saving data...",
          success: "Data saved successfully!",
          error: "Failed to save data.",
        }
      );

      try {
        const response = await axiosPrivate.post(
          `prepare_qc_plumbing/${chassisNo}`
        );
        const data = response.data;

        setAccessoryCheckList(
          (data.accessoryCheckList || []).map((item: QAPlumbingChecklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setGasAndPlanList(
          (data.gasAndPlanList || []).map((item: QAPlumbingChecklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setWaterLeakCheckList(
          (data.waterLeakCheckList || []).map((item: QAPlumbingChecklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setModel(data.model || "");
        setLocalCheckedBy(data.checkedBy || "");
        setCheckedBy(data.checkedBy || "");

        const formattedDate = data.createdOn
          ? new Date(data.createdOn).toLocaleDateString("en-CA", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
          : "";
        setDate(formattedDate || "");
      } catch (error) {
        console.error("Error re-fetching data after save:", error);
        toast.error("Failed to sync data with server.");
      }

      if (checkedBy) {
        const allChecklists = [
          ...accessoryCheckList,
          ...gasAndPlanList,
          ...waterLeakCheckList,
        ];

        let itemNoCounter = 1;
        const newReworkItems = allChecklists
          .filter((item) => item.status === "fail")
          .flatMap((item) => {
            const title = item.title || "No Title Available";
            const notesStr =
              item.notes !== null && item.notes !== undefined ? item.notes : "";

            if (notesStr.trim()) {
              if (notesStr.includes(";")) {
                const parts = notesStr
                  .split(";")
                  .map((part) => part.trim())
                  .filter((part) => part);
                return parts.map((note) => ({
                  itemNo: itemNoCounter++,
                  problemDescription: `${title}: ${note}`,
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                }));
              } else {
                return [
                  {
                    itemNo: itemNoCounter++,
                    problemDescription: `${title}: ${notesStr}`,
                    comment: "",
                    whoFoundFault: checkedBy,
                    responsible: "",
                    whoDidRework: "",
                    timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                    completed: false,
                  },
                ];
              }
            } else {
              return [
                {
                  itemNo: itemNoCounter++,
                  problemDescription: title,
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                },
              ];
            }
          });

        if (newReworkItems.length > 0) {
          const reworkPayload = {
            chassisNo,
            model,
            station_name: "qc_plumbing_rework",
            reworkItems: newReworkItems,
          };

          await axiosPrivate.post(`rework/save`, reworkPayload, {
            params: { rework_type: "qc_plumbing_rework" },
          });
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
      console.log(
        "saveData - loading reset to:",
        false,
        "completeStatus:",
        completeStatus
      );
    }
  };

  const handleRadioChange = (index: number, value: string, section: string) => {
    if (isDisabled && completeStatus === "Complete") {
      console.log(
        "Radio change blocked: isDisabled=",
        isDisabled,
        "completeStatus=",
        completeStatus
      );
      return;
    }

    const updateChecklist = (
      checklist: QAPlumbingChecklist[],
      setChecklist: React.Dispatch<React.SetStateAction<QAPlumbingChecklist[]>>
    ) => {
      const updatedChecklist = [...checklist];
      updatedChecklist[index].status = value.toLowerCase();
      setChecklist(updatedChecklist);
    };

    if (section === "accessory") {
      updateChecklist(accessoryCheckList, setAccessoryCheckList);
    }
    if (section === "gasandplan") {
      updateChecklist(gasAndPlanList, setGasAndPlanList);
    }
    if (section === "waterleak") {
      updateChecklist(waterLeakCheckList, setWaterLeakCheckList);
    }
  };

  const handleNotesChange = (index: number, field: string, value: any) => {
    if (isDisabled && completeStatus === "Complete") {
      console.log(
        "Notes change blocked: isDisabled=",
        isDisabled,
        "completeStatus=",
        completeStatus
      );
      return;
    }

    const updateChecklist = (
      checklist: QAPlumbingChecklist[],
      setChecklist: React.Dispatch<React.SetStateAction<QAPlumbingChecklist[]>>
    ) => {
      const updated = [...checklist];
      updated[index].notes = value;
      setChecklist(updated);
    };

    if (field === "accessory") {
      updateChecklist(accessoryCheckList, setAccessoryCheckList);
    }
    if (field === "gasandplan") {
      updateChecklist(gasAndPlanList, setGasAndPlanList);
    }
    if (field === "waterleak") {
      updateChecklist(waterLeakCheckList, setWaterLeakCheckList);
    }
  };

  const allChecklists = [
    ...accessoryCheckList,
    ...gasAndPlanList,
    ...waterLeakCheckList,
  ];

  const allNotChecked =
    allChecklists.length > 0 &&
    allChecklists.every((item) => item.status?.toLowerCase() === "not checked");

  const checklistsWithSerialNumbers = [
    {
      data: accessoryCheckList,
      section: "accessory",
      subHeaderText: "Check Fitting of Accessories",
    },
    {
      data: gasAndPlanList,
      section: "gasandplan",
      subHeaderText: "Gas and Plan Test",
    },
    {
      data: waterLeakCheckList,
      section: "waterleak",
      subHeaderText: "Check for water leaks at connections",
    },
  ];

  let serialNumberCounter = 0;

  return (
    <Box
      sx={{
        position: "relative",
      }}
    >
      <Typography
        sx={{
          mb: 2,
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color:
            completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>
      <Grid container mt={3}>
        <Grid item md={12}>
          <TableContainer
            sx={{
              position: "relative",
              filter: loading ? "blur(5px)" : "none",
              pointerEvents: loading ? "none" : "auto",
            }}
          >
            <Table aria-label="chassis prep checklist">
              <TableHead>
                <TableRow className="table__head_row">
                  <TableCell
                    className="table__head_cell"
                    sx={{
                      width: "1%",
                      textAlign: "center",
                      verticalAlign: "middle",
                      padding: "0px",
                    }}
                  ></TableCell>
                  <TableCell
                    className="table__head_cell"
                    sx={{
                      width: "1%",
                      textAlign: "center",
                      verticalAlign: "middle",
                      padding: "0px",
                    }}
                  >
                    S/N
                  </TableCell>
                  <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                    CHECKLIST
                  </TableCell>
                  <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                    NOTES
                  </TableCell>
                  <TableCell
                    className="table__head_cell"
                    sx={{ width: "5%" }}
                    align="center"
                  >
                    STATUS
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Box
                        sx={{
                          position: "absolute",
                          top: "60%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          zIndex: 10,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : apiError ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="error">Invalid DC Number</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  checklistsWithSerialNumbers.map((checklistSection) => {
                    const serialNumberStart = serialNumberCounter + 1;
                    serialNumberCounter += checklistSection.data.length;

                    console.log(
                      `Rendering ${checklistSection.section} with serialNumberStart: ${serialNumberStart}, item count: ${checklistSection.data.length}`
                    );

                    return (
                      <React.Fragment key={checklistSection.section}>
                        {checklistSection.data.length > 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              sx={{
                                backgroundColor: "#bdacd1",
                                fontWeight: "bold",
                                textAlign: "center",
                              }}
                            >
                              {checklistSection.subHeaderText}
                            </TableCell>
                          </TableRow>
                        )}
                        {checklistSection.data.map((item, index) => {
                          const shouldBold =
                            !allNotChecked &&
                            item.status?.toLowerCase() === "not checked";
                          const serialNumber = serialNumberStart + index;

                          return (
                            <TableRow key={index} style={{ padding: "0px" }}>
                              <TableCell
                                sx={{
                                  width: "30px",
                                  textAlign: "center",
                                  verticalAlign: "middle",
                                  padding: "4px",
                                  borderLeft: "1px solid #ccc",
                                  borderRight: "1px solid #ccc",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <CameraCapture
                                    chassisDCNumber={"DC" + chassisNo}
                                    checkPointName={
                                      "checkpoint-" + serialNumber
                                    }
                                    deptName="qc_plumbing"
                                    checkCheckpointRecord={
                                      checkCheckpointRecord
                                    }
                                  />
                                  <ImageList
                                    chassisDCNumber={"DC" + chassisNo}
                                    checkPointName={
                                      "checkpoint-" + serialNumber
                                    }
                                    deptName="qc_plumbing"
                                    imagesExist={activeCheckpointList.includes(
                                      "checkpoint-" + serialNumber
                                    )}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell
                                sx={{
                                  width: "40px",
                                  textAlign: "center",
                                  verticalAlign: "middle",
                                  padding: "4px",
                                  borderLeft: "1px solid #ccc",
                                  borderRight: "1px solid #ccc",
                                }}
                              >
                                {serialNumber}
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderLeft: "1px solid #d0d0d0",
                                  fontWeight: shouldBold ? "bold" : "normal",
                                }}
                              >
                                {item.title}
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderLeft: "1px solid #d0d0d0",
                                  wordWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                  verticalAlign: "top",
                                  fontWeight: shouldBold ? "bold" : "normal",
                                }}
                              >
                                <TextField
                                  placeholder="Type the note here..."
                                  variant="standard"
                                  value={item.notes}
                                  onChange={(e) =>
                                    handleNotesChange(
                                      index,
                                      checklistSection.section,
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                  fullWidth
                                  multiline
                                  disabled={
                                    isDisabled && completeStatus === "Complete"
                                  }
                                  InputProps={{
                                    disableUnderline: true,
                                    style: {
                                      fontSize: "14px",
                                      fontWeight: shouldBold
                                        ? "bold"
                                        : "normal",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                className="p-0 px-2"
                                sx={{
                                  borderLeft: "1px solid #d0d0d0",
                                  borderRight: "1px solid #d0d0d0",
                                  textAlign: "center",
                                  verticalAlign: "center",
                                  fontWeight: shouldBold ? "bold" : "normal",
                                }}
                              >
                                <CustomRadioGroup
                                  value={item.status}
                                  onChange={(value) =>
                                    handleRadioChange(
                                      index,
                                      value,
                                      checklistSection.section
                                    )
                                  }
                                  options={[
                                    { label: "Pass", value: "Pass" },
                                    {
                                      label: "Not Checked",
                                      value: "Not Checked",
                                    },
                                    { label: "Fail", value: "Fail" },
                                    { label: "N/A", value: "N/A" },
                                  ]}
                                  disabled={
                                    isDisabled && completeStatus === "Complete"
                                  }
                                  shouldBold={shouldBold}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <Grid container spacing={3} my={4}>
        <Grid item md={4}>
          <TextField
            label="Checked by:"
            variant="outlined"
            value={checkedBy}
            onChange={handleCheckedByChange}
            fullWidth
            disabled={isDisabled && completeStatus === "Complete"}
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            label="Date:"
            variant="outlined"
            type="date"
            value={date}
            onChange={(e) => {
              if (isDisabled && completeStatus === "Complete") {
                console.log(
                  "Date change blocked: isDisabled=",
                  isDisabled,
                  "completeStatus=",
                  completeStatus
                );
                return;
              }
              setDate(e.target.value);
            }}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
            disabled={isDisabled && completeStatus === "Complete"}
          />
        </Grid>
      </Grid>

      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={
          allChecklists.length === 0 ||
          apiError ||
          loading ||
          (!chassisNo && !model) ||
          completeStatus === "Complete"
        }
      >
        Save
      </Button>
    </Box>
  );
};

export default QualityControlPlumbingChecklist;
